import { useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { StickyToc } from '@/components/StickyToc';
import { ContentSections, RelatedGuides } from '@/components/ContentSections';
import { FaqAccordion } from '@/components/FaqAccordion';
import { CtaBanner } from '@/components/CtaBanner';
import { LamalQuickCalculator } from '@/components/LamalQuickCalculator';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/components/Seo';
import { useLang } from '@/i18n/LangContext';
import { toItPath } from '@/i18n/slugs';
import { DISCLAIMER, GUIDE_IMAGES } from '@/data/site';
import { DISCLAIMER_EN } from '@/data/en/site';
import { SALUTE_PAGES } from '@/data/salute';
import { PRIVATI_PAGES } from '@/data/privati';
import { AZIENDE_PAGES, PREVIDENZA_PAGE } from '@/data/aziende';
import { SALUTE_PAGES_EN } from '@/data/en/salute';
import { PRIVATI_PAGES_EN } from '@/data/en/privati';
import { AZIENDE_PAGES_EN, PREVIDENZA_PAGE_EN } from '@/data/en/aziende';
import { getAreaLandingSections } from '@/data/areaContent';
import { PRODUCT_PAGES, PRODUCT_PAGES_EN } from '@/data/productPages';

export const ALL_CONTENT_PAGES = [...SALUTE_PAGES, ...PRIVATI_PAGES, ...AZIENDE_PAGES, PREVIDENZA_PAGE, ...PRODUCT_PAGES];
export const ALL_CONTENT_PAGES_EN = [...SALUTE_PAGES_EN, ...PRIVATI_PAGES_EN, ...AZIENDE_PAGES_EN, PREVIDENZA_PAGE_EN, ...PRODUCT_PAGES_EN];

const AREA_FALLBACK_IMAGES = {
  Salute: '/assets/area-salute.webp',
  Health: '/assets/area-salute.webp',
  Privati: '/assets/area-privati.webp',
  Individuals: '/assets/area-privati.webp',
  Aziende: '/assets/area-aziende.webp',
  Businesses: '/assets/area-aziende.webp',
  Previdenza: '/assets/area-previdenza.webp',
  Pension: '/assets/area-previdenza.webp',
};

export default function ContentPage() {
  const { pathname } = useLocation();
  const { lang, t } = useLang();
  const registry = lang === 'en' ? ALL_CONTENT_PAGES_EN : ALL_CONTENT_PAGES;
  const cleanPath = useMemo(() => {
    if (lang !== 'en') return pathname;
    const noPrefix = pathname.replace(/^\/en(?=\/|$)/, '') || '/';
    return toItPath(noPrefix);
  }, [pathname, lang]);
  const page = useMemo(() => registry.find((p) => p.path === cleanPath), [registry, cleanPath]);

  const breadcrumbs = page ? [
    { label: t('breadcrumbHome'), href: '/' },
    ...(page.kind === 'area'
      ? [{ label: lang === 'en' ? 'Solutions' : 'Soluzioni', href: '/soluzioni' }, { label: page.area }]
      : [
          { label: page.area, href: page.areaPath },
          { label: page.title.length > 42 ? page.title.slice(0, 42) + '…' : page.title },
        ]),
  ] : [];

  const pageImage = page
    ? (GUIDE_IMAGES[page.path] || page.image || AREA_FALLBACK_IMAGES[page.area] || '/assets/home-hero.webp')
    : undefined;
  const seoImage = page && (page.kind === 'guide' || page.kind === 'service') ? pageImage : undefined;
  useSeo({
    title: page ? (page.seoTitle || page.title) : t('notFoundTitle'),
    description: page ? (page.seoDescription || page.summary) : '',
    image: seoImage,
    imageAlt: page?.title,
    type: page?.kind === 'guide' ? 'article' : 'website',
    faq: page?.faq,
    keywords: page?.keywords,
    schemaType: page?.kind === 'area' ? 'CollectionPage' : page?.kind === 'service' ? 'Service' : 'Article',
    breadcrumbs,
    robots: page ? undefined : 'noindex,nofollow',
  });

  if (!page) return <Navigate to={lang === 'en' ? '/en/404' : '/404'} replace />;

  /* breadcrumbs are also passed to the JSON-LD SEO layer */
  const renderedBreadcrumbs = [
    { label: t('breadcrumbHome'), href: '/' },
    ...(page.kind === 'area'
      ? [{ label: lang === 'en' ? 'Solutions' : 'Soluzioni', href: '/soluzioni' }, { label: page.area }]
      : [
          { label: page.area, href: page.areaPath },
          { label: page.title.length > 42 ? page.title.slice(0, 42) + '…' : page.title },
        ]),
  ];

  const tocItems = [
    ...page.sections.filter((s) => s.title).map((s) => ({ id: s.id, label: s.title })),
    ...(page.faq?.length ? [{ id: 'faq', label: t('faqTitle') }] : []),
  ];

  const related = (page.related || [])
    .map((r) => registry.find((p) => p.path === r))
    .filter(Boolean);

  const disclaimer = lang === 'en' ? DISCLAIMER_EN : DISCLAIMER;
  const showLamalCalculator = page.areaPath === '/cassa-malati' || page.path === '/cassa-malati';

  if (page.kind === 'area') {
    const areaSections = getAreaLandingSections(lang, page.path, page.sections);
    return (
      <div data-testid={`content-page-${page.path.replace(/\//g, '-')}`}>
        <PageHero
          eyebrow={page.eyebrow}
          title={page.title}
          summary={page.summary}
          updated={page.updated}
          readingTime={page.readingTime}
          image={pageImage}
          imageAlt={page.title}
          breadcrumbs={renderedBreadcrumbs}
        />

        <section className="bg-brand-deep text-white border-b border-white/10">
          <div className="mx-auto max-w-site px-5 md:px-8 py-16 md:py-20 grid md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-3">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-light">{lang === 'en' ? 'How to read this area' : 'Come leggere quest’area'}</p>
            </div>
            <Reveal className="md:col-span-9 max-w-3xl">
              {page.intro.map((paragraph, index) => (
                <p key={index} className={`leading-relaxed text-white/75 ${index === 0 ? 'text-xl md:text-2xl font-heading font-semibold text-white' : 'text-base md:text-lg mt-5'}`}>{paragraph}</p>
              ))}
            </Reveal>
          </div>
        </section>

        {showLamalCalculator && <LamalQuickCalculator />}

        <ContentSections sections={areaSections} layout="area" inlineCta={page.cta} />

        {page.faq?.length > 0 && (
          <div className="mx-auto max-w-site px-5 md:px-8 py-20 md:py-28">
            <FaqAccordion items={page.faq} eyebrow={null} title={t('faqTitle')} />
            <p data-testid="page-disclaimer" className="mt-12 text-xs leading-relaxed text-muted-foreground border-t border-border pt-6 max-w-3xl">
              {disclaimer}
            </p>
          </div>
        )}

        <div className="mx-auto max-w-site px-5 md:px-8">
          <CtaBanner title={page.cta.title} text={page.cta.text} label={page.cta.label} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`content-page-${page.path.replace(/\//g, '-')}`}>
      <PageHero
        eyebrow={page.eyebrow}
        title={page.title}
        summary={page.summary}
        updated={page.updated}
        readingTime={page.readingTime}
        image={pageImage}
        imageAlt={page.title}
        breadcrumbs={renderedBreadcrumbs}
      />

      {showLamalCalculator && <LamalQuickCalculator />}

      <div className="mx-auto max-w-site px-5 md:px-8 py-14 md:py-20">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-3">
            <StickyToc items={tocItems} />
          </div>
          <div className="lg:col-span-9">
            <div className="max-w-4xl">
              <Reveal className="guide-body mb-14">
                {page.intro.map((p, i) => (
                  <p key={i} className={i === 0 ? 'text-lg md:text-xl font-medium text-foreground/90' : ''}>{p}</p>
                ))}
              </Reveal>
              <ContentSections sections={page.sections} inlineCta={page.cta} />
              <div className="mt-16 md:mt-24">
                {page.faq?.length > 0 && <FaqAccordion items={page.faq} eyebrow={null} title={t('faqTitle')} />}
              </div>
              <p data-testid="page-disclaimer" className="mt-12 text-xs leading-relaxed text-muted-foreground border-t border-border pt-6">
                {disclaimer}
              </p>
              <RelatedGuides pages={related} />
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-site px-5 md:px-8">
        <CtaBanner title={page.cta.title} text={page.cta.text} label={page.cta.label} />
      </div>
    </div>
  );
}
